import { Link } from 'react-router-dom'
import {
  FileText,
  Video,
  Headphones,
  Image,
  File,
  Globe,
  GraduationCap,
  Plus,
  CheckCircle2,
  Sparkles,
  Clock,
  ExternalLink,
} from 'lucide-react'
import type { SearchResult, JournalArticle, WebMediaResource, ResourceType } from '../../types'
import { RESOURCE_TYPE_COLORS } from '../../constants/colors'

interface ResultCardProps {
  result: SearchResult
  onAddToList?: (result: SearchResult) => void
}

const TYPE_ICONS: Record<ResourceType, typeof FileText> = {
  journal: FileText,
  video: Video,
  audio: Headphones,
  image: Image,
  document: File,
  website: Globe,
  course: GraduationCap,
}

const TYPE_LABELS: Record<ResourceType, string> = {
  journal: 'Journal Article',
  video: 'Video',
  audio: 'Audio',
  image: 'Image',
  document: 'Document',
  website: 'Website',
  course: 'Course',
}

function isJournal(result: SearchResult): result is JournalArticle & { _score: number; _source_index: string; cross_domain_subjects?: string[] } {
  return 'record_id' in result
}

function isWebMedia(result: SearchResult): result is WebMediaResource & { _score: number; _source_index: string; cross_domain_subjects?: string[] } {
  return 'resource_id' in result
}

function getResourceId(result: SearchResult): string {
  if (isJournal(result)) return result.record_id
  if (isWebMedia(result)) return result.resource_id
  return ''
}

function getResourceType(result: SearchResult): ResourceType {
  if (isJournal(result)) return 'journal'
  if (isWebMedia(result)) return result.resource_type
  return 'document'
}

function getExcerpt(result: SearchResult): string {
  if (isJournal(result)) {
    return result.tldr || result.abstract || ''
  }
  if (isWebMedia(result)) {
    return result.summary || result.description || ''
  }
  return ''
}

function getAuthors(result: SearchResult): string | null {
  if (isJournal(result) && result.authors.length > 0) {
    if (result.authors.length <= 3) return result.authors.join(', ')
    return `${result.authors.slice(0, 3).join(', ')} et al.`
  }
  if (isWebMedia(result) && result.creator) {
    return result.creator
  }
  return null
}

function getDate(result: SearchResult): string | null {
  if (isJournal(result)) return result.publication_date || null
  if (isWebMedia(result)) return result.publication_date || null
  return null
}

function formatDate(dateStr: string): string {
  try {
    const d = new Date(dateStr)
    return d.toLocaleDateString('en-AU', { year: 'numeric', month: 'short', day: 'numeric' })
  } catch {
    return dateStr
  }
}

function getScoreColor(score: number): string {
  if (score >= 0.8) return 'bg-green-500'
  if (score >= 0.5) return 'bg-yellow-500'
  if (score >= 0.3) return 'bg-orange-500'
  return 'bg-gray-400'
}

export default function ResultCard({ result, onAddToList }: ResultCardProps) {
  const resourceType = getResourceType(result)
  const Icon = TYPE_ICONS[resourceType] || File
  const typeColor = RESOURCE_TYPE_COLORS[resourceType] || '#6B7280'
  const resourceId = getResourceId(result)
  const excerpt = getExcerpt(result)
  const authors = getAuthors(result)
  const date = getDate(result)
  const hasCrossDomain = result.cross_domain_subjects && result.cross_domain_subjects.length > 0

  return (
    <div className="relative rounded-lg border border-gray-200 hover:border-acad-300 p-4 transition bg-white group">
      {/* Score indicator bar */}
      <div className="absolute top-0 left-0 right-0 h-0.5 rounded-t-lg overflow-hidden">
        <div
          className={`h-full ${getScoreColor(result._score)} transition-all`}
          style={{ width: `${Math.min(result._score * 100, 100)}%` }}
        />
      </div>

      <div className="flex items-start gap-3">
        {/* Type icon */}
        <div
          className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center mt-0.5"
          style={{ backgroundColor: `${typeColor}15` }}
        >
          <Icon className="w-4.5 h-4.5" style={{ color: typeColor }} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Type badge row */}
          <div className="flex flex-wrap items-center gap-1.5 mb-1">
            <span
              className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium text-white"
              style={{ backgroundColor: typeColor }}
            >
              {TYPE_LABELS[resourceType]}
            </span>

            {isJournal(result) && result.peer_reviewed && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[11px] font-medium bg-green-100 text-green-700">
                <CheckCircle2 className="w-3 h-3" />
                Peer Reviewed
              </span>
            )}

            {isJournal(result) && result.open_access && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-amber-100 text-amber-700">
                Open Access
              </span>
            )}

            {isWebMedia(result) && result.license_type && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium bg-blue-100 text-blue-700">
                {result.license_type}
              </span>
            )}

            {isWebMedia(result) && result.duration && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[11px] font-medium bg-gray-100 text-gray-600">
                <Clock className="w-3 h-3" />
                {result.duration}
              </span>
            )}

            {hasCrossDomain && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[11px] font-medium bg-violet-100 text-violet-700">
                <Sparkles className="w-3 h-3" />
                AI-discovered
              </span>
            )}
          </div>

          {/* Title */}
          <Link
            to={`/resource/${resourceId}`}
            className="text-base font-semibold text-gray-900 hover:text-acad-600 transition line-clamp-2 leading-snug"
          >
            {result.title}
          </Link>

          {/* Authors / Creator and Date */}
          {(authors || date) && (
            <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
              {authors && <span className="truncate max-w-xs">{authors}</span>}
              {authors && date && <span className="text-gray-300">|</span>}
              {date && <span className="flex-shrink-0">{formatDate(date)}</span>}
            </div>
          )}

          {/* Journal name for articles */}
          {isJournal(result) && result.journal_name && (
            <p className="text-sm text-gray-400 italic mt-0.5 truncate">{result.journal_name}</p>
          )}

          {/* Excerpt */}
          {excerpt && (
            <p className="text-sm text-gray-600 mt-2 line-clamp-3 leading-relaxed">
              {excerpt}
            </p>
          )}

          {/* Citation count for journals */}
          {isJournal(result) && result.citation_count > 0 && (
            <div className="mt-2">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                {result.citation_count.toLocaleString()} citation{result.citation_count !== 1 ? 's' : ''}
              </span>
            </div>
          )}

          {/* Cross-domain subjects */}
          {hasCrossDomain && (
            <div className="flex flex-wrap gap-1 mt-2">
              {result.cross_domain_subjects!.slice(0, 3).map((subject) => (
                <span
                  key={subject}
                  className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] bg-violet-50 text-violet-600 border border-violet-200"
                >
                  {subject}
                </span>
              ))}
              {result.cross_domain_subjects!.length > 3 && (
                <span className="text-[11px] text-violet-500">
                  +{result.cross_domain_subjects!.length - 3} more
                </span>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          {onAddToList && (
            <button
              onClick={() => onAddToList(result)}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-acad-600 border border-acad-300 rounded-md hover:bg-acad-50 transition opacity-0 group-hover:opacity-100 focus:opacity-100"
              title="Add to List"
            >
              <Plus className="w-3 h-3" />
              List
            </button>
          )}
          {(isJournal(result) && result.source_url) || (isWebMedia(result) && result.source_url) ? (
            <a
              href={isJournal(result) ? (result as JournalArticle).source_url : (result as WebMediaResource).source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-acad-500 transition opacity-0 group-hover:opacity-100 focus:opacity-100"
              title="Open source"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          ) : null}
        </div>
      </div>
    </div>
  )
}
